import React, { useState, useRef, useEffect } from 'react';
import { Search, Filter, Download, FileText, Plus, Trash2, Edit2, ChevronLeft, ChevronRight, ChevronDown } from 'lucide-react';
import './Common.css';

const DataTable = ({
    columns = [],
    data = [],
    title = "",

    showSearch = true,
    showFilter = true,
    showExport = true,
    showAddButton = true,
    addButtonLabel = "Add New",
    showStatusToggle = true,
    onAddClick = () => { },
    onSearch = () => { },
    onFilter = () => { },
    onExportCSV = null,
    onExportPDF = null,

    filterOptions = [],

    currentPage = 1,
    totalPages = 10,
    onPageChange = () => { },

    selectedIds = [],
    onSelectionChange = () => { },

    showActions = true,
    showEdit = true,
    showDelete = true,
    onEdit = null,
    onDelete = null,
    onToggleStatus = () => { },
    onRowClick = null,          // optional: fires when a data row (not action buttons) is clicked
    customControls = null,      // custom nodes to render in the control-right section

    rowKey = "id",
}) => {

    const [filterOpen, setFilterOpen] = useState(false);
    const [activeFilter, setActiveFilter] = useState(null);
    const filterRef = useRef(null);

    const [exportOpen, setExportOpen] = useState(false);
    const exportRef = useRef(null);

    useEffect(() => {
        const handleClickOutside = (e) => {
            if (filterRef.current && !filterRef.current.contains(e.target)) {
                setFilterOpen(false);
            }
            if (exportRef.current && !exportRef.current.contains(e.target)) {
                setExportOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const handleFilterSelect = (option) => {
        setActiveFilter(option.value);
        setFilterOpen(false);
        onFilter(option.value);
    };

    const isAllSelected = data.length > 0 && selectedIds.length === data.length;

    // eslint-disable-next-line no-unused-vars
    const handleSelectAll = () => {
        if (isAllSelected) {
            onSelectionChange([]);
        } else {
            onSelectionChange(data.map(item => item.id));
        }
    };

    // eslint-disable-next-line no-unused-vars
    const handleSelectRow = (id) => {
        if (selectedIds.includes(id)) {
            onSelectionChange(selectedIds.filter(itemId => itemId !== id));
        } else {
            onSelectionChange([...selectedIds, id]);
        }
    };

    return (
        <div className="data-table-container">
            {/* Controls Header */}
            {(showSearch || showFilter || showExport || showAddButton) && (
                <div className="table-header-controls">
                    <div className="control-left">
                        {showSearch && (
                            <div className="search-bar-wrapper">
                                <Search className="table-search-icon" size={18} />
                                <input
                                    type="text"
                                    placeholder="Search"
                                    className="table-search-input"
                                    onChange={(e) => onSearch(e.target.value)}
                                />
                            </div>
                        )}
                    </div>

                    <div className="control-right">
                        {customControls}
                        {showFilter && (
                            <div className="filter-dropdown-wrapper" ref={filterRef}>
                                <button
                                    className={`btn-secondary ${activeFilter ? 'btn-secondary--active' : ''}`}
                                    onClick={() => setFilterOpen((prev) => !prev)}
                                >
                                    <Filter size={16} />
                                    {activeFilter
                                        ? (filterOptions.find(o => o.value === activeFilter)?.label || 'Filter')
                                        : 'Filter'}
                                    <ChevronDown size={14} className={`filter-chevron ${filterOpen ? 'filter-chevron--open' : ''}`} />
                                </button>
                                {filterOpen && filterOptions.length > 0 && (
                                    <ul className="filter-dropdown-menu">
                                        {filterOptions.map((option) => (
                                            <li
                                                key={option.value}
                                                className={`filter-dropdown-item ${activeFilter === option.value ? 'filter-dropdown-item--selected' : ''}`}
                                                onClick={() => handleFilterSelect(option)}
                                            >
                                                {option.label}
                                            </li>
                                        ))}
                                        {activeFilter && (
                                            <li
                                                className="filter-dropdown-item filter-dropdown-item--clear"
                                                onClick={() => { setActiveFilter(null); setFilterOpen(false); onFilter(null); }}
                                            >
                                                Clear filter
                                            </li>
                                        )}
                                    </ul>
                                )}
                            </div>
                        )}
                        {showExport && (onExportCSV || onExportPDF) && (
                            <div className="filter-dropdown-wrapper" ref={exportRef}>
                                <button
                                    className={`btn-secondary ${exportOpen ? 'btn-secondary--active' : ''}`}
                                    onClick={() => setExportOpen((prev) => !prev)}
                                >
                                    <Download size={16} />
                                    Export
                                    <ChevronDown size={14} className={`filter-chevron ${exportOpen ? 'filter-chevron--open' : ''}`} />
                                </button>
                                {exportOpen && (
                                    <ul className="filter-dropdown-menu">
                                        {onExportCSV && (
                                            <li
                                                className="filter-dropdown-item"
                                                onClick={() => { setExportOpen(false); onExportCSV(); }}
                                            >
                                                <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                                    <Download size={14} /> Export as CSV
                                                </span>
                                            </li>
                                        )}
                                        {onExportPDF && (
                                            <li
                                                className="filter-dropdown-item"
                                                onClick={() => { setExportOpen(false); onExportPDF(); }}
                                            >
                                                <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                                    <FileText size={14} /> Export as PDF
                                                </span>
                                            </li>
                                        )}
                                    </ul>
                                )}
                            </div>
                        )}
                        {showAddButton && (
                            <button className="btn-primary" onClick={onAddClick}>
                                <Plus size={16} /> {addButtonLabel}
                            </button>
                        )}
                    </div>
                </div>
            )}

            {/* Table */}
            <div className="table-wrapper">
                <table className="common-table">
                    <thead>
                        <tr>
                            {columns.map((col, index) => (
                                <th key={index} style={col.align ? { textAlign: col.align } : {}}>{col.header}</th>
                            ))}
                            {showActions && <th style={{ textAlign: 'right' }}>Actions</th>}
                        </tr>
                    </thead>
                    <tbody>
                        {data.map((row) => (
                            <tr
                                key={row[rowKey] || Math.random()}
                                onClick={() => onRowClick && onRowClick(row)}
                                style={onRowClick ? { cursor: 'pointer' } : {}}
                            >
                                {columns.map((col, idx) => (
                                    <td key={idx} style={col.align ? { textAlign: col.align } : {}}>
                                        {(() => {
                                            const value = col.render ? col.render(row) : row[col.accessor];
                                            return (value === null || value === undefined || value === "") ? "-" : value;
                                        })()}
                                    </td>
                                ))}
                                {showActions && (
                                    <td
                                        style={{ textAlign: 'right' }}
                                        onClick={(e) => e.stopPropagation()}
                                    >
                                        <div className="action-buttons" style={{ justifyContent: 'flex-end' }}>
                                            {showEdit && onEdit && (
                                                <button className="action-btn edit-btn" onClick={() => onEdit(row)}>
                                                    <Edit2 size={18} />
                                                </button>
                                            )}
                                            {showDelete && onDelete && (
                                                <button className="action-btn delete-btn" onClick={() => onDelete(row)}>
                                                    <Trash2 size={18} />
                                                </button>
                                            )}
                                            {showStatusToggle && (
                                                <label className="switch">
                                                    <input
                                                        type="checkbox"
                                                        checked={row.isActive}
                                                        onChange={() => onToggleStatus(row)}
                                                    />
                                                    <span className="slider round"></span>
                                                </label>
                                            )}
                                        </div>
                                    </td>
                                )}
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {/* Pagination */}
            <div className="pagination-container">
                <button
                    className="nav-btn"
                    disabled={currentPage === 0}
                    onClick={() => onPageChange(currentPage - 1)}
                >
                    <ChevronLeft size={16} /> Previous
                </button>


                {(() => {
                    const pages = [];
                    if (totalPages <= 5) {
                        // Show all pages when total pages are 5 or less
                        for (let i = 0; i < totalPages; i++) pages.push(i);
                    } else {
                        // Always show first page
                        pages.push(0);
                        const left = Math.max(1, currentPage - 1);
                        const right = Math.min(totalPages - 2, currentPage + 1);
                        if (left > 1) pages.push('ellipsis');
                        for (let i = left; i <= right; i++) pages.push(i);
                        if (right < totalPages - 2) pages.push('ellipsis');
                        // Always show last page
                        pages.push(totalPages - 1);
                    }
                    return (
                        <div className="page-numbers">
                            {pages.map((p, idx) => {
                                if (p === 'ellipsis') {
                                    return <span key={'ellipsis' + idx} className="ellipsis">…</span>;
                                }
                                return (
                                    <button
                                        key={p}
                                        className={`page-btn ${currentPage === p ? 'active' : ''}`}
                                        onClick={() => onPageChange(p)}
                                    >
                                        {p + 1}
                                    </button>
                                );
                            })}
                        </div>
                    );
                })()}

                <button
                    className="nav-btn"
                    disabled={currentPage === totalPages - 1}
                    onClick={() => onPageChange(currentPage + 1)}
                >
                    Next <ChevronRight size={16} />
                </button>
            </div>
        </div>
    );
};

export default DataTable;
